/**
 * PeopleCountingResults
 *
 * Design: Clean metrics dashboard with animated counters
 * - Hero metric card for main people count
 * - Grid layout for video statistics
 * - Detection confidence visualization
 */
import React from "react";
import { PeopleCountingResults as ResultsType } from "../../types";
import { formatNumber } from "../../utils";
import {
  Users,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Video,
  Activity,
  Target,
} from "lucide-react";

interface PeopleCountingResultsProps {
  results: ResultsType;
}

export const PeopleCountingResults: React.FC<PeopleCountingResultsProps> = ({
  results,
}) => {
  const isVideo = results.type === "video";
  const avgConfidence = results.detections?.length
    ? (
        (results.detections.reduce((sum, d) => sum + d.confidence, 0) /
          results.detections.length) *
        100
      ).toFixed(1)
    : null;

  return (
    <div className="space-y-6">
      {/* Hero Metric */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-500/20 via-slate-800/50 to-accent-500/20 border border-primary-500/30 p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent-500/10 rounded-full blur-2xl" />

        <div className="relative z-10 flex items-center gap-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/30">
            <Users className="w-10 h-10 text-white" />
          </div>
          <div>
            <p className="text-slate-400 text-sm font-medium mb-1">
              {isVideo ? "Average People Count" : "People Detected"}
            </p>
            <h2 className="text-6xl font-bold text-white tracking-tight">
              {formatNumber(
                isVideo
                  ? results.average_people_count || 0
                  : results.people_count || 0
              )}
            </h2>
            {avgConfidence && (
              <p className="text-slate-400 text-sm mt-2 flex items-center gap-1">
                <Target className="w-4 h-4 text-emerald-400" />
                {avgConfidence}% avg confidence
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Video Statistics */}
      {isVideo && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<TrendingUp className="w-5 h-5" />}
            label="Maximum"
            value={formatNumber(results.max_people_count || 0)}
            color="emerald"
          />
          <StatCard
            icon={<TrendingDown className="w-5 h-5" />}
            label="Minimum"
            value={formatNumber(results.min_people_count || 0)}
            color="amber"
          />
          <StatCard
            icon={<BarChart3 className="w-5 h-5" />}
            label="Frames Processed"
            value={formatNumber(results.total_frames_processed || 0)}
            color="violet"
          />
          <StatCard
            icon={<Activity className="w-5 h-5" />}
            label="Detection Rate"
            value={`${(
              (results.total_frames_processed || 0) /
              (results.video_duration_seconds || 1)
            ).toFixed(1)} FPS`}
            color="cyan"
          />
        </div>
      )}

      {/* Video Metadata */}
      {isVideo && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Video className="w-5 h-5 text-primary-400" />
            <h3 className="text-white font-semibold">Video Information</h3>
          </div>
          <div className="grid grid-cols-3 gap-6 text-sm">
            <div>
              <p className="text-slate-400 mb-1">Duration</p>
              <p className="text-white font-medium">
                {results.video_duration_seconds}s
              </p>
            </div>
            <div>
              <p className="text-slate-400 mb-1">Frame Rate</p>
              <p className="text-white font-medium">{results.video_fps} FPS</p>
            </div>
            <div>
              <p className="text-slate-400 mb-1">Resolution</p>
              <p className="text-white font-medium">
                {results.video_resolution}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Detection Confidence (for images) */}
      {!isVideo && results.detections && results.detections.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              <h3 className="text-white font-semibold">Detection Confidence</h3>
            </div>
            <span className="text-slate-400 text-sm">
              {results.detections.length} objects
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {results.detections.slice(0, 20).map((det, idx) => (
              <span
                key={idx}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  det.confidence >= 0.8
                    ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                    : det.confidence >= 0.6
                    ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                    : "bg-slate-700/50 text-slate-400 border border-slate-600/30"
                }`}
              >
                {(det.confidence * 100).toFixed(0)}%
              </span>
            ))}
            {results.detections.length > 20 && (
              <span className="px-3 py-1.5 text-slate-500 text-sm">
                +{results.detections.length - 20} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: "emerald" | "amber" | "violet" | "cyan" | "rose";
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color }) => {
  const colorClasses = {
    emerald:
      "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-400",
    amber:
      "from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-400",
    violet:
      "from-violet-500/20 to-violet-500/5 border-violet-500/30 text-violet-400",
    cyan: "from-cyan-500/20 to-cyan-500/5 border-cyan-500/30 text-cyan-400",
    rose: "from-rose-500/20 to-rose-500/5 border-rose-500/30 text-rose-400",
  };

  return (
    <div
      className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-5`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className={colorClasses[color].split(" ").pop()}>{icon}</span>
        <span className="text-slate-400 text-sm">{label}</span>
      </div>
      <p
        className={`text-3xl font-bold ${colorClasses[color].split(" ").pop()}`}
      >
        {value}
      </p>
    </div>
  );
};
